/* eslint-disable import/no-cycle */
import { AxiosRequestConfig } from 'axios';
import {
  IResponse,
  IData,
  ISpacesContact,
  ISearch,
  IRequestSpacesContact,
  IGroup,
  IGroupData,
  IGroupCoverPhotoUpload, IGroupInvitation, IAddUsersToGroup, IRequestSpaces, IRequestSpacesGroup,
} from '@/types/interfaces';
import ApiService from '@/services/ApiService';
import { useMainStore } from '@/store/mainStore';
// import { ACCOUNT_STATUS, CONTACT_STATUS, REGISTRATION_STATE } from '@/types/enums';

// function getMockContact(): INetworkContact {
//   return {
//     accountId: String(Math.random()),
//     email: 'notrealmail@mailmail.co',
//     accountStatuses: [ACCOUNT_STATUS.REGISTERED],
//     registrationState: REGISTRATION_STATE.CONFIRM_EMAIL,
//     photo: { url: '', id: '' },
//     npi: null,
//     firstName: 'Front Mock',
//     lastName: 'User',
//     speciality: 'Mock',
//     location: 'Mock, location, mock',
//     contactStatus: CONTACT_STATUS.ACCEPTED,
//   };
// }

// function getTenMockContacts(): INetworkContact[] {
//   const arr: INetworkContact[] = [];
//   for (let i = 0; i < 10; i += 1) {
//     arr.push(getMockContact());
//   }
//   return arr;
// }

export default class SpacesService {
  public static fetchOutgoingContact(): Promise<IResponse<IData<IRequestSpacesContact[]>>> {
    return ApiService.getAndReturnData('contact/request/list/outgoing');
  }

  public static fetchOutgoingGroup(): Promise<IResponse<IData<IGroupData[]>>> {
    return ApiService.getAndReturnData('group/requests/enter/my');
  }

  public static fetchInvitationsContact(): Promise<IResponse<IData<IRequestSpacesContact[]>>> {
    return ApiService.getAndReturnData('contact/request/list/incoming');
  }

  public static fetchInvitationsGroup(): Promise<IResponse<IData<IRequestSpacesGroup[]>>> {
    return ApiService.getAndReturnData('group/requests/incoming/my');
  }

  public static fetchInvitationsAll(): Promise<IResponse<IData<IRequestSpaces[]>>> {
    return ApiService.getAndReturnData('user/requests/incoming/group-with-contact');
  }

  public static fetchMyContacts(): Promise<IResponse<IData<IRequestSpacesContact[]>>> {
    return ApiService.getAndReturnData('contact/list/my');
  }

  public static fetchSuggestions(): Promise<IResponse<IData<ISpacesContact[]>>> {
    return ApiService.getAndReturnData('search/users/suggested');
  }

  /** Return a list with users who want to join the group where current user is admin or owner */
  public static fetchRequestsToMyGroups(): Promise<IResponse<IData<IRequestSpacesGroup[]>>> {
    return ApiService.getAndReturnData('group/requests/enter');
  }

  /** Return a list with users who want to join the group */
  public static fetchRequestsToGroup(groupId: string): Promise<IResponse<IData<IRequestSpacesGroup[]>>> {
    return ApiService.getAndReturnData(`group/requests/enter/${groupId}`);
  }

  public static acceptContact(contactId: string) {
    return ApiService.post(`contact/request/accept/${contactId}`);
  }

  /** Accepts group request. */
  public static acceptRequestToGroup(groupId: string, partnerId: string) {
    return ApiService.post(`group/requests/accept/${groupId}/${partnerId}`);
  }

  /** Accepts request from user who wants to join the group. */
  public static acceptUserToGroup(groupId: string, partnerId: string) {
    return ApiService.post(`group/requests/enter/accept/${groupId}/${partnerId}`);
  }

  public static rejectContact(contactId: string) {
    return ApiService.post(`contact/request/reject/${contactId}`);
  }

  /** Rejects group request. */
  public static rejectRequestToGroup(groupId: string, partnerId: string) {
    return ApiService.post(`group/requests/reject/${groupId}/${partnerId}`);
  }

  /** Rejects request from user who wants to join the group. */
  public static rejectUserToGroup(groupId: string, partnerId: string) {
    return ApiService.post(`group/requests/enter/reject/${groupId}/${partnerId}`);
  }

  public static requestContact(contactId: string) {
    return ApiService.post(`contact/request/create/${contactId}`);
  }

  /** Creates a request to enter the group. This request creates a user then admin can accept or reject it. */
  public static requestToJoinGroup(groupId: string) {
    return ApiService.post(`group/requests/enter/${groupId}`);
  }

  public static deleteContact(contactId: string) {
    return ApiService.delete(`contact/remove/${contactId}`);
  }

  public static addContactsToGroup(groupId: string, usersId: string[]) {
    return ApiService.post(`group/joinUsersIntoGroup/${groupId}`, usersId);
  }

  public static deleteUserFromGroup(groupId: string, userId: string): Promise<IResponse<IData<any>>> {
    return ApiService.postAndReturnData(`group/deleteUserFromGroup/${groupId}/${userId}`);
  }

  public static deleteUsersFromGroup(groupId: string, usersId: string[]): Promise<IResponse<IData<any>>> {
    return ApiService.postAndReturnData(`group/removeUsersFromGroup/${groupId}`, usersId);
  }

  public static inviteUsersToGroup(payload: IGroupInvitation[]): Promise<IResponse<IData<any>>> {
    return ApiService.postAndReturnData('group/requests/bunch', payload);
  }

  public static inviteUserToGroup(payload: IGroupInvitation): Promise<IResponse<IData<any>>> {
    return ApiService.postAndReturnData('group/requests', payload);
  }

  public static getInvitedUserToGroup(groupId: string): Promise<IResponse<IData<[{accountId: string}]>>> {
    return ApiService.getAndReturnData(`group/requests/invited/${groupId}`);
  }

  public static addUsersToGroup(groupId:string, payload: IAddUsersToGroup[]): Promise<IResponse<any>> {
    return ApiService.postAndReturnData(`group/updateUserList/${groupId}`, payload);
  }

  public static fetchGroupsByOwnerId(ownerId: string): Promise<IResponse<IData<IGroup[]>>> {
    return ApiService.getAndReturnData(`group/groupsByOwnerId/${ownerId}`);
  }

  public static fetchParticipatedGroupsByUser(userId: string): Promise<IResponse<IData<IGroup[]>>> {
    return ApiService.getAndReturnData(`group/getUserGroups/${userId}`);
  }

  public static fetchMyGroups(): Promise<IResponse<IData<IGroup[]>>> {
    const mainStore = useMainStore();
    return ApiService.getAndReturnData(`/group/groupsByOwnerId/${mainStore.user.accountId}`);
  }

  public static fetchAllGroups(): Promise<IResponse<IData<IGroupData[]>>> {
    return ApiService.getAndReturnData('group/me/AllGroups');
  }

  public static leaveGroup(payload: any): Promise<IResponse> {
    return ApiService.postAndReturnData('group/me/leave/', payload);
  }

  public static createGroup(payload: any): Promise<IResponse<IData<IGroupData>>> {
    return ApiService.postAndReturnData('group/createGroup', payload);
  }

  public static updateGroup(payload: any): Promise<IResponse<any>> {
    return ApiService.postAndReturnData('group/updateGroup', payload);
  }

  public static deleteGroup(groupId: string): Promise<IResponse<any>> {
    return ApiService.postAndReturnData(`/group/deleteGroup/${groupId}`);
  }

  public static getGroupById(groupId: string): Promise<IResponse<IData<IGroupData>>> {
    return ApiService.getAndReturnData(`group/${groupId}`);
  }

  public static updateUserStatus(payload: { userId: string, groupId: string, type: string }): Promise<IResponse<any>> {
    return ApiService.postAndReturnData('group/updateStatus', payload);
  }

  public static searchContact(query: string, pageIndex: number, pageSize: number): Promise<IResponse<IData<ISearch<ISpacesContact[]>>>> {
    // const mock: IData<ISearch<INetworkContact[]>> = {
    //   data: {
    //     content: getTenMockContacts(),
    //     first: pageIndex === 0,
    //     last: pageIndex === 2,
    //     number: pageIndex,
    //     numberOfElements: 25,
    //     size: pageSize,
    //     sort: [],
    //     totalElements: 25,
    //     totalPages: 3,
    //   },
    // };
    // const response: IResponse<IData<ISearch<INetworkContact[]>>> = {
    //   errorKey: null, message: null, results: mock, success: true,
    // };
    // return Promise.resolve(response);
    const params: AxiosRequestConfig = { params: { query, pageIndex, pageSize } };
    return ApiService.getAndReturnData('search/users', params);
  }

  public static searchGroups(name: string): Promise<IResponse<IData<IGroupData[]>>> {
    return ApiService.getAndReturnData(`group/search/${name}`);
  }

  public static saveGroupCoverPhoto(payload: IGroupCoverPhotoUpload): Promise<IResponse<IData<IGroupData>>> {
    return ApiService.postAndReturnData('group/photo/upload', payload);
  }
}
